import { HTMLAttributes, forwardRef, useEffect, useRef } from 'react';
import { IconType } from 'react-icons';
import classNames from 'classnames';
import {
    useTimelineCondensePellets,
    useTimelineInterval,
} from 'app/agriculture/AgTimeline/context/TimelineContext';
import { useStackingContext } from 'app/agriculture/AgTimeline/components/TimelinePellet/StackingContext/StackingContext';

export interface TimelinePelletContentProps {
    id: string;
    text: string;
    Icon: IconType;
    color?: 'primary' | 'warning' | 'danger';
    disabled?: boolean;
}

export const TimelinePelletContent = forwardRef<
    HTMLButtonElement,
    HTMLAttributes<HTMLButtonElement> & TimelinePelletContentProps
>(function TimelinePelletContent(
    { id, Icon, text, color = 'primary', disabled = false, ...htmlProps },
    ref,
) {
    const condensed = useTimelineCondensePellets();
    const timelineInterval = useTimelineInterval();
    const stackingContext = useStackingContext();

    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (containerRef.current != null) {
            stackingContext.addContainer(id, containerRef.current);
        }

        return () => {
            stackingContext.removeContainer(id);
        };
    }, [id, containerRef, timelineInterval]);

    return (
        <button
            ref={ref}
            {...htmlProps}
            data-testid="TimelinePelletContent"
            className={classNames('TimelinePelletContent', `TimelinePelletContent--${color}`, {
                'TimelinePelletContent--disabled': disabled,
            })}
        >
            <div ref={containerRef} className="TimelinePelletContent__inner">
                {!condensed && (
                    <>
                        <div className="TimelinePelletContent__icon">
                            <Icon />
                        </div>
                        <span className="TimelinePelletContent__text">{text}</span>
                    </>
                )}
            </div>
        </button>
    );
});
